import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import { getSupabaseAuthHeaders } from "@/lib/supabaseAuth";

interface SubscriptionState {
  subscribed: boolean;
  productId: string | null;
  subscriptionEnd: string | null;
  loading: boolean;
}

// Product ID to tier mapping
const PRODUCT_TIERS: Record<string, string> = {
  'prod_TXw2zbIcWziifH': 'starter',
  'prod_TXw218UdkpfROa': 'professional',
  'prod_TXw2sEsDlC3ABl': 'business',
};

// Features available per tier (cumulative - higher tiers include lower tier features)
const TIER_FEATURES: Record<string, string[]> = {
  free: [],
  starter: ['recording'],
  professional: ['recording', 'breakout_rooms'],
  business: ['recording', 'breakout_rooms', 'analytics'],
};

export function useSubscription() {
  const { user } = useAuth();
  const [state, setState] = useState<SubscriptionState>({
    subscribed: false,
    productId: null,
    subscriptionEnd: null,
    loading: true,
  });

  const fetchSubscription = useCallback(async () => {
    if (!user) {
      setState({
        subscribed: false,
        productId: null,
        subscriptionEnd: null,
        loading: false,
      });
      return;
    }

    try {
      const authHeaders = await getSupabaseAuthHeaders();
      if (!authHeaders) {
        setState({
          subscribed: false,
          productId: null,
          subscriptionEnd: null,
          loading: false,
        });
        return;
      }

      const { data, error } = await supabase.functions.invoke('check-subscription', {
        headers: authHeaders,
      });
      if (error) throw error;
      
      setState({
        subscribed: data?.subscribed ?? false,
        productId: data?.product_id ?? null,
        subscriptionEnd: data?.subscription_end ?? null,
        loading: false,
      });
    } catch (error) {
      console.error('Error fetching subscription:', error);
      setState(prev => ({ ...prev, loading: false }));
    }
  }, [user]);

  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  const getTier = useCallback((): string => {
    if (!state.subscribed || !state.productId) return 'free';
    return PRODUCT_TIERS[state.productId] || 'free';
  }, [state.subscribed, state.productId]);

  const hasFeature = useCallback((feature: string): boolean => {
    // All features are now accessible to all users
    return true;
  }, []);

  // All premium features are now open for all users
  const canUseRecording = useCallback(() => true, []);
  const canUseBreakoutRooms = useCallback(() => true, []);
  const canUseAnalytics = useCallback(() => true, []);

  return {
    ...state,
    tier: getTier(),
    hasFeature,
    canUseRecording,
    canUseBreakoutRooms,
    canUseAnalytics,
    refetch: fetchSubscription,
  };
}
